import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Button, Select, SelectChangeEvent, useFlashMessage } from '@indigo-ag/component-module';
import {
  ContractStatus,
  FinancingMunicipality,
  FinancingState,
  GuaranteeData as GuaranteeDataType,
  GuaranteeDataInput
} from '@indigo-ag/schema';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import i18next from 'core/i18n';
import { EditableNumberField, EditableTextField, GridContainer, GridContainerItem, usePrompt, utils } from 'shared';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import {
  financingMunicipalitiesByState,
  financingStates,
  upsertFinancingAgFinancing
} from 'features/brazil-grower/queries';

type GuaranteeUpdateData = Pick<
  GuaranteeDataType,
  'comments' | 'product' | 'pricePerBag' | 'bagsPerHectares' | 'value100Percent'
>;

export const GuaranteeData: React.FunctionComponent = () => {
  /**
   * Context
   */
  const { agFinance } = useContext(FinanceProcessContext);

  const getInitialGuaranteeData = useCallback((): GuaranteeUpdateData => {
    return {
      comments: agFinance?.guaranteeData?.comments || '',
      product: agFinance?.guaranteeData?.product || '',
      pricePerBag: agFinance?.guaranteeData?.pricePerBag,
      bagsPerHectares: agFinance?.guaranteeData?.bagsPerHectares,
      value100Percent: agFinance?.guaranteeData?.value100Percent
    };
  }, [agFinance]);

  /**
   * State
   */
  const [guaranteeDataUpdated, setGuaranteeDataUpdated] = useState<GuaranteeUpdateData>(getInitialGuaranteeData());
  const [isUpdatingInformation, setIsUpdatingInformation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedState, setSelectedState] = useState(agFinance?.guaranteeData?.state?.id || '0');
  const [selectedMunicipality, setSelectedMunicipality] = useState(agFinance?.guaranteeData?.municipality?.id || '0');
  const [warrantyProductInputError, setWarrantyProductInputError] = useState('');
  const [stateSelectError, setStateSelectError] = useState('');
  const [municipalitySelectError, setMunicipalitySelectError] = useState('');
  const [commentsInputError, setCommentsInputError] = useState('');
  /**
   * Queries
   */
  const { data: statesData } = useQuery<{ financingStates: FinancingState[] }>(financingStates);
  const { data: municipalitiesData } = useQuery<{ financingMunicipalities: FinancingMunicipality[] }>(
    financingMunicipalitiesByState,
    {
      variables: { stateId: Number(selectedState) }
    }
  );

  /**
   * Mutations
   */
  const [upsertAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  /**
   * Callbacks
   */
  const updateGuaranteeData = (guaranteeData: GuaranteeDataInput) => {
    return upsertAgFinancingFn({
      variables: {
        data: {
          id: agFinance?.id,
          guaranteeData
        }
      }
    });
  };

  const opportunity = agFinance?.orderInfo?.opportunities[0];
  const matriculasWithLegalAnalysis =
    agFinance?.phase2?.matriculas?.filter(m => m.includedInAnalysis && m.details?.some(d => d.id != null)) || [];
  const matriculasNumbers = matriculasWithLegalAnalysis
    .filter(m => !!m.details?.find(d => d.id != null)?.registrationNumbers)
    .map(m => m.details?.find(d => d.id != null)?.registrationNumbers)
    .join(', ');

  const matriculasMunicipalities = matriculasWithLegalAnalysis
    .filter(m => !!m.details?.find(d => d.id != null)?.municipality?.name)
    .map(m => m.details?.find(d => d.id != null)?.municipality?.name)
    .join(', ');

  const updateGuaranteeTextInfo = (prop: keyof GuaranteeUpdateData) => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      const updatedGuaranteeData = {
        ...guaranteeDataUpdated,
        [prop]: event.target.value
      };
      setGuaranteeDataUpdated(updatedGuaranteeData);
      setIsUpdatingInformation(true);
    };
  };

  const updateGuaranteeNumberInfo = (prop: keyof GuaranteeUpdateData) => {
    return (value: number | null): void => {
      const updatedGuaranteeData = {
        ...guaranteeDataUpdated,
        [prop]: value || 0
      };
      setGuaranteeDataUpdated(updatedGuaranteeData);
      setIsUpdatingInformation(true);
    };
  };

  const handleStateChange = (event: SelectChangeEvent<string>): void => {
    setSelectedState(event.target.value || '0');
    if (!event.target.value) {
      setSelectedMunicipality('0');
    }
    setIsUpdatingInformation(true);
  };

  const handleMunicipalityChange = (event: SelectChangeEvent<string>): void => {
    setSelectedMunicipality(event.target.value || '0');
    setIsUpdatingInformation(true);
  };

  const { addFlashMessage } = useFlashMessage();

  const clearInputErrorStates = () => {
    if (warrantyProductInputError) setWarrantyProductInputError('');
    if (stateSelectError) setStateSelectError('');
    if (municipalitySelectError) setMunicipalitySelectError('');
    if (commentsInputError) setCommentsInputError('');
  };

  /**
   * Runs simple validation checks against several inputs, setting error states if necessary
   *
   * @returns true if all validations pass, false otherwise
   */
  const validateInputs = (): boolean => {
    clearInputErrorStates();
    let validationPassed = true;
    const errorMessage = i18next.t('inputFieldRequiredError');
    if (!guaranteeDataUpdated.product) {
      setWarrantyProductInputError(errorMessage);
      validationPassed = false;
    }
    if (!selectedState || selectedState === '0') {
      setStateSelectError(errorMessage);
      validationPassed = false;
    }
    if (!selectedMunicipality || selectedMunicipality === '0') {
      setMunicipalitySelectError(errorMessage);
      validationPassed = false;
    }
    if (!guaranteeDataUpdated.comments) {
      setCommentsInputError(errorMessage);
      validationPassed = false;
    }

    return validationPassed;
  };

  const updateGuaranteeInfo = () => {
    if (agFinance?.guaranteeData?.id && updateGuaranteeData) {
      if (!validateInputs()) return;
      setIsSaving(true);

      const selectedStateId = Number(selectedState);
      const selectedMunicipalityId = Number(selectedMunicipality);

      updateGuaranteeData({
        id: agFinance.guaranteeData.id,
        state: selectedStateId > 0 ? { id: selectedStateId } : undefined,
        municipality: selectedMunicipalityId > 0 ? { id: selectedMunicipalityId } : undefined,
        ...guaranteeDataUpdated
      })
        .then(() => {
          setIsUpdatingInformation(false);
          addFlashMessage({
            content: utils.text.firstLetterUppercase(i18next.t('guaranteeDataUpdatedSuccessfully')),
            title: '',
            variant: 'success'
          });
        })
        .catch((e: ApolloError) => {
          addFlashMessage({
            content: e.graphQLErrors[0].message || i18next.t('error'),
            title: '',
            variant: 'error'
          });
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const isGuaranteeDataReadOnly =
    agFinance?.phase3?.contractStatus &&
    ![ContractStatus.WAITING_DRAFT, ContractStatus.CREATING_AMENDMENT].includes(agFinance.phase3.contractStatus);

  useEffect(() => {
    setGuaranteeDataUpdated(getInitialGuaranteeData());
  }, [isGuaranteeDataReadOnly, getInitialGuaranteeData]);

  usePrompt(!isGuaranteeDataReadOnly && isUpdatingInformation);

  const gridItems: GridContainerItem[] = [
    {
      label: i18next.t('warrantyArea'),
      value: opportunity?.collateralHectares ? `${utils.parseNumberToLocale(opportunity.collateralHectares)} Ha` : '-'
    },
    {
      label: i18next.t('collateralBags'),
      value: utils.parseNumberToLocale(Math.ceil(opportunity?.collateralBags || 0)) || '-'
    },
    {
      label: i18next.t('warrantyType'),
      value: opportunity?.collateralType
    },
    {
      label: i18next.t('collateralDueDate'),
      value: opportunity?.collateralDueDate ? utils.date.format({ date: opportunity.collateralDueDate }) : '-'
    },
    {
      label: i18next.t('contractValue'),
      value: opportunity?.contractValue
        ? utils.currency.inputMaskCurrency(opportunity.contractValue, {
            useComaForDecimals: true
          })
        : '-'
    },
    {
      label: i18next.t('warrantyAmount'),
      value: opportunity?.collateralValue ? utils.currency.formatBrazilCurrency(opportunity.collateralValue) : '-'
    },
    {
      customField: (
        <EditableNumberField
          label={'value 100 %'}
          name="value100Percentage"
          onNumberChange={updateGuaranteeNumberInfo('value100Percent')}
          numberValue={Number(guaranteeDataUpdated.value100Percent) || undefined}
          readonly={isGuaranteeDataReadOnly}
        />
      )
    },
    {
      customField: (
        <EditableTextField
          label={i18next.t('warrantyProduct')}
          value={guaranteeDataUpdated.product || ''}
          onChange={updateGuaranteeTextInfo('product')}
          readonly={isGuaranteeDataReadOnly}
          error={warrantyProductInputError}
        />
      )
    },
    {
      label: i18next.t('state'),
      value: agFinance?.guaranteeData?.state?.name,
      customField: !isGuaranteeDataReadOnly ? (
        <Select
          label={i18next.t('state')}
          value={selectedState}
          name="stateId"
          options={statesData ? statesData.financingStates.map(s => ({ name: s.name, value: s.id })) : []}
          onChange={handleStateChange}
          sizeVariant="slim"
          error={stateSelectError}
        />
      ) : (
        undefined
      )
    },
    {
      label: i18next.t('municipality'),
      value: agFinance?.guaranteeData?.municipality?.name,
      customField: !isGuaranteeDataReadOnly ? (
        <Select
          label={i18next.t('municipality')}
          value={selectedMunicipality}
          name="municipalityId"
          options={
            Number(selectedState) > 0 && municipalitiesData
              ? municipalitiesData.financingMunicipalities.map(m => ({
                  name: m.name,
                  value: m.id
                }))
              : []
          }
          onChange={handleMunicipalityChange}
          sizeVariant="slim"
          error={municipalitySelectError}
        />
      ) : (
        undefined
      )
    },
    {
      label: i18next.t('tradingName'),
      value: opportunity?.tradingName
    },
    {
      label: i18next.t('tradingAddress'),
      value: opportunity?.tradingAddress
    },

    {
      label: utils.text.toTitleCase(i18next.t('matriculasNumbers')),
      value: matriculasNumbers
    },
    {
      label: utils.text.toTitleCase(i18next.t('municipalities')),
      value: matriculasMunicipalities
    },
    {
      customField: (
        <EditableNumberField
          label={i18next.t('bagsPriceBase')}
          name="bagsPriceBase"
          onNumberChange={updateGuaranteeNumberInfo('pricePerBag')}
          numberValue={Number(guaranteeDataUpdated.pricePerBag) || undefined}
          readonly={isGuaranteeDataReadOnly}
        />
      )
    },
    {
      customField: (
        <EditableNumberField
          label={i18next.t('bagsBase')}
          name="bagsBase"
          onNumberChange={updateGuaranteeNumberInfo('bagsPerHectares')}
          numberValue={Number(guaranteeDataUpdated.bagsPerHectares) || undefined}
          readonly={isGuaranteeDataReadOnly}
        />
      )
    }
  ];
  return (
    <div>
      <GridContainer columns={4} items={gridItems} />
      <div className="mt-2 width-50">
        <EditableTextField
          label={i18next.t('comments')}
          value={guaranteeDataUpdated.comments || ''}
          onChange={updateGuaranteeTextInfo('comments')}
          readonly={isGuaranteeDataReadOnly}
          error={commentsInputError}
        />
      </div>
      {!isGuaranteeDataReadOnly ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={updateGuaranteeInfo}
            type="submit"
            size="small"
            busy={isSaving}
            disabled={!isUpdatingInformation}
          >
            {i18next.t('save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
